import { supabase, isSupabaseConfigured } from '../lib/supabase';

export type ToolReview = {
  id: string;
  tool_id: string;
  user_id: string;
  rating: number;
  title: string;
  body: string;
  created_at: string;
  updated_at: string;
};

export type ToolReviewStats = {
  tool_id: string;
  review_count: number;
  avg_rating: number;
};

export async function fetchToolReviews(toolId: string): Promise<ToolReview[]> {
  if (!isSupabaseConfigured()) return [];
  const { data, error } = await supabase
    .from('tool_reviews')
    .select('*')
    .eq('tool_id', toolId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) {
    console.warn('[reviews] fetch failed', error.message);
    return [];
  }
  return (data as ToolReview[]) ?? [];
}

export async function fetchToolReviewStats(
  toolId: string
): Promise<ToolReviewStats | null> {
  if (!isSupabaseConfigured()) return null;
  const { data, error } = await supabase
    .from('tool_review_stats')
    .select('*')
    .eq('tool_id', toolId)
    .maybeSingle();
  if (error) {
    console.warn('[reviews] stats fetch failed', error.message);
    return null;
  }
  return (data as ToolReviewStats) ?? null;
}

export async function fetchMyReview(
  toolId: string,
  userId: string
): Promise<ToolReview | null> {
  if (!isSupabaseConfigured()) return null;
  const { data, error } = await supabase
    .from('tool_reviews')
    .select('*')
    .eq('tool_id', toolId)
    .eq('user_id', userId)
    .maybeSingle();
  if (error) return null;
  return (data as ToolReview) ?? null;
}

export async function upsertMyReview(params: {
  toolId: string;
  userId: string;
  rating: number;
  title: string;
  body: string;
}): Promise<{ error: string | null }> {
  if (!isSupabaseConfigured()) {
    return { error: 'Database not configured' };
  }
  const payload = {
    tool_id: params.toolId,
    user_id: params.userId,
    rating: params.rating,
    title: params.title.slice(0, 120),
    body: params.body.slice(0, 4000),
    updated_at: new Date().toISOString(),
  };
  const { error } = await supabase
    .from('tool_reviews')
    .upsert(payload, { onConflict: 'tool_id,user_id' });
  return { error: error?.message ?? null };
}

export async function deleteMyReview(
  toolId: string,
  userId: string
): Promise<{ error: string | null }> {
  if (!isSupabaseConfigured()) {
    return { error: 'Database not configured' };
  }
  const { error } = await supabase
    .from('tool_reviews')
    .delete()
    .eq('tool_id', toolId)
    .eq('user_id', userId);
  return { error: error?.message ?? null };
}
